import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, User as UserIcon, Search } from '../../icons';
import { Avatar } from '../../components/shared';
import { tTask } from '../../lib/task-i18n';

/**
 * 화면구성도 V0.1 슬라이드 9 (2026-08-18) — 목록보기의 **담당자·기한 셀**.
 *
 * 제안은 두 가지였다: "담당자·기한은 **아이콘으로** 처리", "담당자·기한은 **팝업으로** 처리".
 * 즉 값이 비어 있어도 셀이 빈칸('-')이 아니라 **무엇을 넣는 자리인지 알려 주는 아이콘**이어야
 * 하고, 편집은 다른 화면으로 가지 않고 그 자리에 뜨는 팝오버로 끝나야 한다.
 *
 * 셀 컴포넌트를 표(TaskTableView)에서 떼어 둔 이유는 팝오버 상태(열림·검색어·달력 월)가
 * 행마다 따로 놀아야 해서다 — 표가 그 상태를 다 들면 렌더가 통째로 흔들린다.
 */

export interface CellMember {
  userId: string;
  name: string;
  avatarUrl: string | null;
  /** 부서/직책 — 검색 대상(설계 참조의 "이름, 부서 또는 메일 주소로 검색"). */
  subtitle?: string | null;
}

/**
 * 팝오버 위치 — **뷰포트 고정 좌표**로 띄운다.
 *
 * 표(.tt-wrap)가 `overflow: auto` 스크롤 컨테이너라, 팝오버를 셀 기준 absolute 로 두면
 * 표 경계에서 잘린다(달력 오른쪽 절반이 사라졌다). 버튼의 화면 좌표를 재서 fixed 로 띄우고,
 * 오른쪽·아래로 넘칠 것 같으면 반대편으로 접어 붙인다.
 */
function useAnchorPos(open: boolean, width: number, height: number) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!open || !btnRef.current) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = r.bottom + 6;
    const top = below + height > window.innerHeight - 8
      ? Math.max(8, r.top - height - 6)
      : below;
    setPos({ top, left });
  }, [open, width, height]);
  return { btnRef, pos };
}

/** 팝오버 바깥 클릭·Esc 로 닫기 — 두 셀이 같은 규칙을 쓴다. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close(); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

interface AssigneeCellProps {
  assignees: string[];
  members: CellMember[];
  disabled?: boolean;
  onChange: (userIds: string[]) => void;
}

/** 담당자 셀 — 비면 사람 아이콘, 누르면 검색 팝오버에서 다중 선택. */
export function AssigneeCell({ assignees, members, disabled, onChange }: AssigneeCellProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useDismiss(open, () => setOpen(false));
  const { btnRef, pos } = useAnchorPos(open, 260, 300);

  const byId = useMemo(() => {
    const m: Record<string, CellMember> = {};
    for (const x of members) m[x.userId] = x;
    return m;
  }, [members]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter((m) => `${m.name} ${m.subtitle ?? ''}`.toLowerCase().includes(s));
  }, [members, q]);

  function toggle(userId: string) {
    onChange(assignees.includes(userId)
      ? assignees.filter((id) => id !== userId)
      : [...assignees, userId]);
  }

  return (
    <div className="tcp-wrap" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`tcp-cell${assignees.length === 0 ? ' tcp-cell--empty' : ''}`}
        disabled={disabled}
        aria-expanded={open}
        aria-label={tTask('task.cell.assignee_aria')}
        data-tip={tTask('task.cell.assignee_aria')}
        onClick={() => setOpen((v) => !v)}
      >
        {assignees.length === 0 ? (
          <UserIcon size={13} aria-hidden />
        ) : (
          <span className="tcp-avatars">
            {assignees.slice(0, 3).map((uid) => (
              <Avatar
                key={uid}
                userId={Number(uid) || 0}
                name={byId[uid]?.name ?? ''}
                src={byId[uid]?.avatarUrl ?? null}
                size="xxs"
              />
            ))}
            {assignees.length > 3 && <em className="tcp-more">+{assignees.length - 3}</em>}
          </span>
        )}
      </button>

      {open && pos && (
        <div
          className="tcp-pop tcp-pop--people"
          style={{ top: pos.top, left: pos.left }}
          role="dialog"
          aria-label={tTask('task.cell.assignee_aria')}
        >
          <div className="tcp-search">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tTask('task.cell.assignee_search_ph')}
              aria-label={tTask('task.cell.assignee_search_ph')}
              autoFocus
            />
            <Search size={13} aria-hidden />
          </div>
          <ul className="tcp-people scrollbar-thin">
            {filtered.length === 0 && <li className="tcp-empty">{tTask('task.cell.no_member')}</li>}
            {filtered.map((m) => {
              const on = assignees.includes(m.userId);
              return (
                <li key={m.userId}>
                  <button type="button" className={`tcp-person${on ? ' tcp-person--on' : ''}`} onClick={() => toggle(m.userId)}>
                    <Avatar userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl} size="xs" />
                    <span className="tcp-person-body">
                      <b>{m.name}</b>
                      {m.subtitle && <small>{m.subtitle}</small>}
                    </span>
                    {on && <span className="tcp-check" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
          {assignees.length > 0 && (
            <button type="button" className="tcp-clear" onClick={() => onChange([])}>
              {tTask('task.cell.clear_all')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 팝오버 선택지로 쓸 사람 — CellMember 보다 느슨하다.
 *
 * 커스텀필드(custom-field-ui)가 넘겨 주는 멤버 목록에는 아바타·부서가 없다(`{userId, name}`).
 * 그쪽까지 같은 팝오버를 쓰게 하려고 두 필드를 optional 로 연다 — CellMember 는 그대로 대입된다.
 */
export interface PickerMember {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  subtitle?: string | null;
}

interface MemberPickerCellProps {
  /** 고른 사람의 userId. 없으면 null. */
  value: string | null;
  members: PickerMember[];
  /** 필드 이름 — 버튼·팝오버의 접근성 이름에 쓴다. */
  label: string;
  disabled?: boolean;
  onChange: (userId: string | null) => void;
}

/**
 * 사람 **한 명**을 고르는 셀 (2026-08-21) — 커스텀필드 MEMBER 타입용.
 *
 * 종전에는 이 자리가 native `<select>` 였다. 같은 표의 담당자 칸은 이미 팝오버인데
 * 옆 칸만 OS 기본 드롭다운이 튀어나와, 생김새도 다르고 검색도 안 됐다(2026-08-21 상사 지적).
 * 담당자 셀(AssigneeCell)과 **같은 팝오버·같은 위치 계산·같은 닫기 규칙**을 쓴다.
 * 다른 점은 하나뿐이다 — 다중 선택이 아니라 하나를 고르면 바로 닫힌다.
 */
export function MemberPickerCell({ value, members, label, disabled, onChange }: MemberPickerCellProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useDismiss(open, () => setOpen(false));
  const { btnRef, pos } = useAnchorPos(open, 260, 300);

  const picked = useMemo(
    () => members.find((m) => m.userId === value) ?? null,
    [members, value],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter((m) => `${m.name} ${m.subtitle ?? ''}`.toLowerCase().includes(s));
  }, [members, q]);

  function pick(userId: string | null) {
    onChange(userId);
    setOpen(false);
  }

  const aria = tTask('task.cell.member_aria', { field: label });

  return (
    <div className="tcp-wrap" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`tcp-cell${picked ? '' : ' tcp-cell--empty'}`}
        disabled={disabled}
        aria-expanded={open}
        aria-label={aria}
        data-tip={aria}
        onClick={() => setOpen((v) => !v)}
      >
        {picked ? (
          <span className="tcp-one">
            <Avatar userId={Number(picked.userId) || 0} name={picked.name} src={picked.avatarUrl ?? null} size="xxs" />
            <em>{picked.name}</em>
          </span>
        ) : (
          <UserIcon size={13} aria-hidden />
        )}
      </button>

      {open && pos && (
        <div
          className="tcp-pop tcp-pop--people"
          style={{ top: pos.top, left: pos.left }}
          role="dialog"
          aria-label={aria}
        >
          <div className="tcp-search">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tTask('task.cell.assignee_search_ph')}
              aria-label={tTask('task.cell.assignee_search_ph')}
              autoFocus
            />
            <Search size={13} aria-hidden />
          </div>
          <ul className="tcp-people scrollbar-thin">
            {filtered.length === 0 && <li className="tcp-empty">{tTask('task.cell.no_member')}</li>}
            {filtered.map((m) => {
              const on = m.userId === value;
              return (
                <li key={m.userId}>
                  <button type="button" className={`tcp-person${on ? ' tcp-person--on' : ''}`} onClick={() => pick(m.userId)}>
                    <Avatar userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl ?? null} size="xs" />
                    <span className="tcp-person-body">
                      <b>{m.name}</b>
                      {m.subtitle && <small>{m.subtitle}</small>}
                    </span>
                    {on && <span className="tcp-check" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
          {picked && (
            <button type="button" className="tcp-clear" onClick={() => pick(null)}>
              {tTask('task.cell.clear_one')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface DueCellProps {
  dueDate: string | null;
  startDate: string | null;
  /** 지연 표시 — 표가 이미 판정한 값을 그대로 받는다(판정 규칙이 두 군데로 갈라지지 않게). */
  overdue: boolean;
  label: string | null;
  disabled?: boolean;
  onChange: (next: { dueDate?: string | null; startDate?: string | null }) => void;
}

/** 기한 셀 — 비면 달력 아이콘, 누르면 달력 팝오버(마감일 · 시작일 · 모두 지우기). */
export function DueCell({ dueDate, startDate, overdue, label, disabled, onChange }: DueCellProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'due' | 'start'>('due');
  const [cursor, setCursor] = useState(() => monthStart(dueDate ?? todayIso()));
  const ref = useDismiss(open, () => setOpen(false));
  const { btnRef, pos } = useAnchorPos(open, 250, 360);

  const cells = useMemo(() => monthCells(cursor), [cursor]);

  function pick(iso: string) {
    onChange(mode === 'due' ? { dueDate: iso } : { startDate: iso });
    setOpen(false);
    setMode('due');
  }

  return (
    <div className="tcp-wrap" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`tcp-cell${dueDate ? '' : ' tcp-cell--empty'}${overdue ? ' tcp-cell--over' : ''}`}
        disabled={disabled}
        aria-expanded={open}
        aria-label={tTask('task.cell.due_aria')}
       
        onClick={() => setOpen((v) => !v)}
      >
        {dueDate ? (label ?? dueDate) : <Calendar size={13} aria-hidden />}
      </button>

      {open && pos && (
        <div
          className="tcp-pop tcp-pop--cal"
          style={{ top: pos.top, left: pos.left }}
          role="dialog"
          aria-label={tTask('task.cell.due_aria')}
        >
          <div className="tcp-cal-head">
            <b>{tTask('task.cell.year_month', { y: String(cursor.getFullYear()), m: String(cursor.getMonth() + 1) })}</b>
            <span className="tcp-cal-nav">
              <button type="button" onClick={() => setCursor(monthStart(todayIso()))}>{tTask('task.cell.today')}</button>
              <button type="button" aria-label={tTask('task.cell.prev_month')} data-tip={tTask('task.cell.prev_month')} onClick={() => setCursor(addMonth(cursor, -1))}>‹</button>
              <button type="button" aria-label={tTask('task.cell.next_month')} data-tip={tTask('task.cell.next_month')} onClick={() => setCursor(addMonth(cursor, 1))}>›</button>
            </span>
          </div>

          <div className="tcp-cal-grid" role="grid">
            {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
              <span key={d} className={`tcp-cal-dow${i === 0 ? ' tcp-cal-dow--sun' : ''}`}>{d}</span>
            ))}
            {cells.map((c) => (
              <button
                key={c.iso}
                type="button"
                className={[
                  'tcp-cal-day',
                  c.outside ? 'tcp-cal-day--out' : '',
                  c.iso === todayIso() ? 'tcp-cal-day--today' : '',
                  c.iso === dueDate ? 'tcp-cal-day--due' : '',
                  c.iso === startDate ? 'tcp-cal-day--start' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => pick(c.iso)}
              >
                {c.day}
              </button>
            ))}
          </div>

          <button
            type="button"
            className={`tcp-cal-mode${mode === 'start' ? ' tcp-cal-mode--on' : ''}`}
            onClick={() => setMode(mode === 'start' ? 'due' : 'start')}
          >
            {mode === 'start' ? tTask('task.cell.picking_start') : tTask('task.cell.add_start')}
          </button>

          {(dueDate || startDate) && (
            <button
              type="button"
              className="tcp-clear"
              onClick={() => { onChange({ dueDate: null, startDate: null }); setOpen(false); }}
            >
              {tTask('task.cell.clear_all')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 날짜 유틸 (로컬 기준) ───────────────────────────────────

function pad(n: number): string { return n < 10 ? `0${n}` : String(n); }

export function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayIso(): string { return toIso(new Date()); }

function monthStart(iso: string): Date {
  const d = new Date(iso);
  const base = Number.isNaN(d.getTime()) ? new Date() : d;
  return new Date(base.getFullYear(), base.getMonth(), 1);
}

function addMonth(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

/** 달력 6주 격자 — 앞뒤 달 날짜는 outside 로 표시(설계 참조의 회색 날짜). */
export function monthCells(cursor: Date): Array<{ iso: string; day: number; outside: boolean }> {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const out: Array<{ iso: string; day: number; outside: boolean }> = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    out.push({ iso: toIso(d), day: d.getDate(), outside: d.getMonth() !== cursor.getMonth() });
  }
  return out;
}
